<template>
  <div
    class="d-flex align-center pt-4"
    :style="{
      height: display.height - 112 + 'px',
      overflowY: 'auto',
    }"
  >
    <v-container class="text-left" style="max-width: 1024px">
      <h1 class="text-h5 font-weight-bold mb-2">Select your area</h1>
      <p class="text-body-1 text-medium-emphasis mb-8">
        Pick the area of interest that will form the primary focus of your
        report. Can't see your area listed?
        <a
          href="https://ocsi.uk/about/contact-us/?utm_source=on-demand&utm_medium=buttonr&utm_campaign=pre-sale"
          target="_blank"
          >Contact us</a
        >
        and we'll help
      </p>

      <div class="text-body-2 font-weight-medium mb-2">Choose your area</div>
      <v-autocomplete
        v-model="selectedArea"
        v-model:search="search"
        :loading="searchingArea"
        :items="searchResults"
        :no-data-text="searchingArea ? 'Searching' : 'No results'"
        item-title="name"
        item-value="id"
        return-object
        no-filter
        placeholder="Search areas"
        rounded="0"
        density="comfortable"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        autofocus
        autocomplete="off"
      >
        <template v-slot:item="{ item: areaGroup }">
          <v-list density="compact" class="w-100">
            <div class="w-100">
              <!-- Standard areas -->
              <div>
                <span
                  class="text-h6 w-100 font-weight-medium px-3"
                  style="font-size: 16px !important"
                >
                  {{ areaGroup.value.area_group }}
                </span>
                <v-list-item
                  v-for="area in areaGroup.value.areas"
                  :key="area.id"
                  class="hoverable-item w-100"
                  @click="plotAreaOnMap(area)"
                  :style="{
                    backgroundColor:
                      selectedArea?.id === area.id ? 'rgba(187, 222, 251)' : '',
                  }"
                  :label="area.name"
                  :value="area.id"
                >
                  <v-list-item-title class="ml-3">{{
                    area.name
                  }}</v-list-item-title>
                </v-list-item>
              </div>
            </div>
          </v-list>
        </template>
      </v-autocomplete>

      <div class="text-body-2 font-weight-medium mb-2">
        Preview of selected area
      </div>

      <v-card
        v-if="!selectedArea"
        variant="outlined"
        class="preview-card d-flex align-center justify-center"
        :style="{
          height: display.height - 382 + 'px',
          maxHeight: '666px',
          backgroundColor: '#F9F9F9',
        }"
      >
        <!-- only hide the preview text when an area is selected -->
        <div class="d-flex flex-column align-center text-center">
          <v-icon size="48" class="mb-4 text-medium-emphasis"
            >mdi-map-marker-outline</v-icon
          >
          <h3
            class="text-h6 font-weight-bold text-black mb-2 text-medium-emphasis"
          >
            Select your focus area
          </h3>
          <p class="text-body-2 text-medium-emphasis" style="max-width: 350px">
            Type the name of your report focus area into the search above and
            we'll show it on the map so you can check it's the right one.
          </p>
        </div>
      </v-card>
      <v-card>
        <!-- MapComponent is always mounted, so this.$refs.myMap is never undefined -->
        <MapComponent
          v-show="selectedArea"
          ref="myMap"
          :showMapControls="false"
          :roadMapBoundaryStyle="{
            fillColor: '#273B77',
            fillOpacity: 0.2,
            strokeColor: '#273B77',
            strokeWeight: 3,
          }"
          :height="display.height - 382 + 'px'"
          maxHeight="666px !important"
          width="100%"
        />
      </v-card>

      <v-app-bar location="bottom" rounded="0" class="bottom-bar">
        <v-btn
          variant="text"
          rounded="0"
          class="ml-4 d-none d-sm-flex"
          style="color: #3d5769"
          @click="$router.replace({ name: 'Report outline' })"
          >GO BACK</v-btn
        >
        <v-spacer class="d-none d-sm-flex" />

        <!-- Mobile layout: price on one line, button below -->
        <div
          class="d-flex d-sm-none flex-column align-center justify-center flex-grow-1 pb-4"
        >
          <div class="d-flex align-center justify-center my-0 py-1">
            <span class="order-subtotal-label mr-2">ORDER SUBTOTAL</span>
            <span class="text-h6 font-weight-bold" style="color: #4d4d4d">
              £495.00
            </span>
          </div>
          <div class="d-flex justify-center px-4 w-100">
            <v-btn
              variant="elevated"
              :disabled="!selectedArea"
              @click="continueToCheckout()"
              rounded="0"
              class="pa-3 flex-grow-1"
              color="#7ad6d9"
              >CONTINUE</v-btn
            >
          </div>
        </div>

        <!-- Desktop layout: spaced out -->
        <div class="text-center d-none d-sm-block">
          <div class="order-subtotal-label">ORDER SUBTOTAL</div>
          <div class="text-h6 font-weight-bold" style="color: #4d4d4d">
            £495.00
          </div>
        </div>
        <v-spacer class="d-none d-sm-flex" />
        <v-btn
          variant="elevated"
          :disabled="!selectedArea"
          @click="continueToCheckout()"
          rounded="0"
          color="#7ad6d9"
          class="pa-3 mr-4 d-none d-sm-flex"
          style="width: 220px"
          >CONTINUE</v-btn
        >
      </v-app-bar>
    </v-container>
  </div>
</template>

<script>
import MapComponent from "../components/Map.vue";
import { systemMessages } from "@/mixins/SystemMessages";
import { useDisplay } from "vuetify";

export default {
  name: "ImdSelectArea",
  mixins: [systemMessages],
  data() {
    return {
      display: useDisplay(),
      search: "",
      searchingArea: false,
      searchResults: [],
      searchIteration: null,
    };
  },
  components: {
    MapComponent,
  },
  mounted() {
    if (this.selectedArea) {
      this.plotAreaOnMap(this.selectedArea, false);
    }

    // Send visit to analytics
    this.$axios.put("/send-analytics", {
      event: "on_demand_iod_select_area_visits",
    });
  },
  computed: {
    selectedArea: {
      get() {
        const area =
          this.$route.query?.custom_area ?? this.$route.query?.standard_area;

        return area ? JSON.parse(area) : null;
      },
      set(area) {
        if (area) {
          this.$router.push({
            query: {
              [area.category_name ? "custom_area" : "standard_area"]:
                JSON.stringify(area),
            },
          });
        }
      },
    },
  },
  methods: {
    async areaSearch() {
      if (!this.search) return;

      this.searchingArea = true;
      try {
        const response = await this.$axios.get(
          `/on-demand-area-search/${this.search}`,
        );

        this.searchResults = response.data;
      } catch (error) {
        this.errorMsg({
          title: "Error fetching areas",
          message: error?.response?.data?.message,
          timeout: 6000,
        });
        console.error(error);
      } finally {
        this.searchingArea = false;
      }
    },
    async plotAreaOnMap(area, hidePreviousArea = true) {
      if (!area) return;

      const prevId = this.selectedArea?.id;
      this.selectedArea = area;
      setTimeout(() => {
        document.querySelector(".continue-button")?.focus();
      }, 100);

      const action = area.category_name // is custom area or standard area?
        ? "displayCustomAreas"
        : "displayStandardAreas";
      await this.$refs.myMap[action](area.id);

      // zoom in
      const zoomAction = area.category_name
        ? "zoomInCustomAreas"
        : "zoomInStandardAreas";
      await this.$refs.myMap[zoomAction](area.id);

      // hide previously selected area
      if (hidePreviousArea) {
        this.$refs.myMap.hideCustomAreas(prevId);
        this.$refs.myMap.hideStandardAreas(prevId);
      }
    },
    continueToCheckout() {
      if (!this.selectedArea) return;

      this.$router.push({ name: "OnDemandCheckOut", query: this.$route.query });
    },
  },
  watch: {
    search(val, oldVal) {
      if (val && val !== oldVal && val !== this.selectedArea?.name) {
        clearTimeout(this.searchIteration);

        this.searchIteration = setTimeout(() => {
          this.areaSearch();
        }, 500);
      }
    },
  },
};
</script>

<style scoped>
.preview-card {
  border-color: #e0e0e0;
}

.v-btn--variant-outlined {
  border-color: #e0e0e0;
}
.hoverable-item {
  transition: background-color 0.4s ease-in-out;
  cursor: pointer;
}
.hoverable-item:hover {
  background-color: rgba(187, 222, 251);
  transition: background-color 0.4s ease-in-out;
}
.bottom-bar :deep(.v-toolbar__content) {
  min-height: 64px !important;
  height: auto !important;
}
.order-subtotal-label {
  color: #4d4d4d;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}
</style>
